import { useEffect, useState, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { Toaster } from "sonner";
import Welcome from "./pages/Welcome";
import Assessment from "./pages/Assessment";
import Results from "./pages/Results";
import PaymentSuccess from "./pages/PaymentSuccess";
import Plan from "./pages/Plan";
import AdminSetup from "./pages/admin/AdminSetup";
import AdminLogin from "./pages/admin/AdminLogin";
import AdminPasswordReset from "./pages/admin/AdminPasswordReset";
import AdminApp from "./pages/admin/AdminApp";
import SalonLanding from "./pages/salon/SalonLanding";
import SalonRegister from "./pages/salon/SalonRegister";
import SalonLogin from "./pages/salon/SalonLogin";
import SalonDashboard from "./pages/salon/SalonDashboard";
import SalonAssessment from "./pages/salon/SalonAssessment";
import SalonResult from "./pages/salon/SalonResult";
import SalonSubscriptionSuccess from "./pages/salon/SalonSubscriptionSuccess";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Always send credentials so admin cookies work
axios.defaults.withCredentials = true;

const RTL_LANGS = new Set(["ar", "fa"]);

const detectBrowserLang = (activeCodes) => {
  const stored = localStorage.getItem("kt_lang");
  if (stored && activeCodes.has(stored)) return stored;
  const nav = (navigator.languages || [navigator.language || "en"])
    .map((l) => (l || "").toLowerCase().split("-")[0]);
  for (const l of nav) if (activeCodes.has(l)) return l;
  return activeCodes.has("en") ? "en" : Array.from(activeCodes)[0] || "en";
};

export default function App() {
  const [lang, setLang] = useState("en");
  const [languages, setLanguages] = useState([
    { code: "en", name: "English", native: "English", rtl: false, active: true },
    { code: "it", name: "Italian", native: "Italiano", rtl: false, active: true },
  ]);

  useEffect(() => {
    axios
      .get(`${API}/languages`)
      .then((r) => {
        const items = r.data?.items || [];
        if (items.length) setLanguages(items);
        const active = new Set(items.filter((l) => l.active).map((l) => l.code));
        if (active.size) setLang(detectBrowserLang(active));
      })
      .catch(() => {
        const active = new Set(["en", "it"]);
        setLang(detectBrowserLang(active));
      });
  }, []);

  useEffect(() => {
    const meta = languages.find((l) => l.code === lang);
    const rtl = meta?.rtl || RTL_LANGS.has(lang);
    const el = document.documentElement;
    el.setAttribute("lang", lang);
    el.setAttribute("dir", rtl ? "rtl" : "ltr");
  }, [lang, languages]);

  const changeLang = useCallback((l) => {
    setLang(l);
    localStorage.setItem("kt_lang", l);
  }, []);

  return (
    <div className="App min-h-screen bg-white text-[#383633]">
      <Toaster position="top-center" richColors />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Welcome lang={lang} setLang={changeLang} languages={languages} />} />
          <Route path="/assessment" element={<Assessment lang={lang} setLang={changeLang} languages={languages} />} />
          <Route path="/results/:resultId" element={<Results lang={lang} setLang={changeLang} languages={languages} />} />
          <Route path="/payment/success" element={<PaymentSuccess lang={lang} />} />
          <Route path="/plan/:resultId" element={<Plan lang={lang} setLang={changeLang} languages={languages} />} />
          <Route path="/admin/setup" element={<AdminSetup />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/reset" element={<AdminPasswordReset />} />
          <Route path="/admin/*" element={<AdminApp />} />
          <Route path="/salon" element={<SalonLanding />} />
          <Route path="/salon/register" element={<SalonRegister />} />
          <Route path="/salon/login" element={<SalonLogin />} />
          <Route path="/salon/dashboard" element={<SalonDashboard />} />
          <Route path="/salon/assessment" element={<SalonAssessment />} />
          <Route path="/salon/result" element={<SalonResult />} />
          <Route path="/salon/subscription/success" element={<SalonSubscriptionSuccess />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}
